import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { FoodRecipe } from '@/services/recipes/types';
import { getFoodRecipes } from '@/services/recipes/recipeService';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { supabase } from '@/integrations/supabase/client';

interface CountryResponse {
  name: string;
  id?: string;
}

interface RegionResponse {
  name: string;
  id?: string;
}

interface ProfileWithLocation {
  country?: CountryResponse;
  region?: RegionResponse;
}

const processRecipeData = (recipeData: any): FoodRecipe => {
  const processBenefitsField = (field: string | string[] | null): string[] => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [field];
    } catch (e) {
      return [field];
    }
  };

  return {
    ...recipeData,
    benefits: processBenefitsField(recipeData.benefits),
    benefits_en: processBenefitsField(recipeData.benefits_en),
    benefits_es: processBenefitsField(recipeData.benefits_es),
    benefits_pt: processBenefitsField(recipeData.benefits_pt),
  };
};

export function FoodRecommendationsTab() {
  const { i18n, t } = useTranslation();
  const [recipes, setRecipes] = useState<FoodRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadRecipes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data: allRecipes, error: recipesError } = await supabase
          .from('food_recipes')
          .select('*, country:country_id(name), region:region_id(name)');
          
        if (recipesError) {
          throw recipesError;
        }
        
        setRecipes(allRecipes ? allRecipes.map(recipe => processRecipeData(recipe)) : []);
      } catch (err: any) {
        console.error('Error al cargar recetas:', err);
        toast.error('Error al cargar recetas');
        setError('Hubo un problema al cargar las recetas.');
      } finally {
        setLoading(false);
      }
    };
    
    loadRecipes();
  }, [i18n.language]);

  const handleRecipeClick = (id: string) => {
    console.log(`Navigating to recipe with ID: ${id}`);
    navigate(`/food/${id}`);
  };

  function getTranslatedRecipe(recipe: FoodRecipe) {
    const currentLang = i18n.language;
    return {
      ...recipe,
      name: recipe[`name_${currentLang}`] || recipe.name,
      description: recipe[`description_${currentLang}`] || recipe.description,
    };
  }

  if (loading) {
    return (
      <div className="space-y-4 pb-20">
        {[1, 2, 3].map((index) => (
          <Card key={index} className="overflow-hidden">
            <Skeleton className="h-40 w-full" />
            <CardContent className="p-4">
              <Skeleton className="h-6 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full mb-3" />
              <div className="flex gap-2 mb-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 pb-20">
        <Alert>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <p className="text-center py-4">
          Intenta recargar la página para ver si las recetas están disponibles.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      <p className="text-sm text-gray-600 mb-4">
        {t('recipesForAnemia')}
      </p>
      
      {recipes.map((recipe) => {
        const translatedRecipe = getTranslatedRecipe(recipe);
        return (
          <Card 
            key={recipe.id} 
            className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleRecipeClick(recipe.id)}
          >
            <div className="h-40 bg-gray-200">
              <img 
                src={recipe.image_url || "/placeholder.svg"} 
                alt={translatedRecipe.name} 
                className="w-full h-full object-cover" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </div>
            <CardContent className="p-4">
              <h3 className="font-medium text-lg mb-2">{translatedRecipe.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{translatedRecipe.description}</p>
              <div className="flex gap-2 mb-3">
                <Badge variant="secondary" className="bg-gray-100 text-gray-700">{recipe.preparation_time}</Badge>
                <Badge variant="secondary" className="bg-gray-100 text-gray-700">{recipe.difficulty}</Badge>
                <Badge variant="secondary" className="bg-gray-100 text-gray-700">{recipe.serving_size}</Badge>
              </div>
              <Button className="w-full" variant="outline">
                {t('viewRecipe')}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
